import Cookies from 'js-cookie';
import { Cookie } from 'constants/appCookies';
import { pharmacyContexts } from 'constants/contexts';
import { status } from 'constants/general';

export const PORTAL_ROLES = {
  PHARMACY_OWNER: 'PharmacyOwner',
  PHARMACIST: 'Pharmacist',
};

export const ROLE_RESOLUTION_STATUS = {
  LOADING: 'LOADING',
  RESOLVED: 'RESOLVED',
  DENIED: 'DENIED',
  FAILED: 'FAILED',
};

const PHARMACIST_ROLES = {
  Pharmacist: 6,
  PharmacyEmployee: 31,
};
const PHARMACY_OWNER_ROLES = {
  PharmacyOwner: 39,
  ChainOwner: 41,
};
const VPAY_CONTEXTS = [pharmacyContexts.vpay, pharmacyContexts.vpaySettings];

const isRoleId = (roles, roleId) => Object.values(roles).includes(roleId);

const isRoleName = (roles, roleName) =>
  Object.keys(roles).some(name => name.toLowerCase() === roleName);

export const parseJwt = token => {
  try {
    let base64String = token.split('.')[1];
    base64String = base64String.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64String)
        .split('')
        .map(c => `%${`00${c.charCodeAt(0).toString(16)}`.slice(-2)}`)
        .join(''),
    );
    return JSON.parse(jsonPayload);
  } catch (err) {
    return '';
  }
};

export const validateAuthCookie = shouldSaveRedirectUrl => {
  const cookie = Cookies.get(Cookie.AUTH_TOKEN);
  let validity = false;
  let token = '';
  if (cookie) {
    token = parseJwt(cookie);
    if (token && token.nameid) {
      validity = true;
    }
  } else if (shouldSaveRedirectUrl) {
    Cookies.set(Cookie.RETURN_URL, window.location.href);
  }
  return validity;
};

export const getAuthCookieInfo = () => {
  const authCookie = Cookies.get(Cookie.AUTH_TOKEN);
  const authToken = parseJwt(authCookie);
  const { nameid: userKey, ownerkey: pharmacyKey } = authToken;

  return {
    userKey,
    authCookie,
    pharmacyKey,
  };
};

export const getUpperLevelDomain = () => {
  let domain = '';
  if (process.env.NODE_ENV === 'production') {
    const url = window.location.href;
    const domainArr = new URL(url).hostname.split('.');
    domain = domainArr.slice(-2).join('.');
  }
  return domain;
};

const normalizeSingleRoleCandidate = candidate => {
  if (candidate === null || candidate === undefined || candidate === '') {
    return null;
  }

  if (typeof candidate === 'object') {
    const nameMatch = normalizeSingleRoleCandidate(candidate.name ?? candidate.roleName);
    const idMatch = normalizeSingleRoleCandidate(candidate.id ?? candidate.roleId);
    if (nameMatch && idMatch && nameMatch !== idMatch) {
      return 'CONFLICT';
    }
    return nameMatch || idMatch;
  }

  if (typeof candidate === 'number') {
    if (isRoleId(PHARMACY_OWNER_ROLES, candidate)) return PORTAL_ROLES.PHARMACY_OWNER;
    if (isRoleId(PHARMACIST_ROLES, candidate)) return PORTAL_ROLES.PHARMACIST;
    return 'UNKNOWN';
  }

  if (typeof candidate === 'string') {
    const trimmed = candidate.trim();
    if (!trimmed) return null;
    const lower = trimmed.toLowerCase();
    if (isRoleName(PHARMACY_OWNER_ROLES, lower)) {
      return PORTAL_ROLES.PHARMACY_OWNER;
    }
    if (isRoleName(PHARMACIST_ROLES, lower)) {
      return PORTAL_ROLES.PHARMACIST;
    }
    if (/^\d+$/.test(trimmed)) {
      const numericRole = Number(trimmed);
      if (isRoleId(PHARMACY_OWNER_ROLES, numericRole)) return PORTAL_ROLES.PHARMACY_OWNER;
      if (isRoleId(PHARMACIST_ROLES, numericRole)) return PORTAL_ROLES.PHARMACIST;
      return 'UNKNOWN';
    }
    return 'UNKNOWN';
  }

  return 'UNKNOWN';
};

const combineRoleCandidates = candidates => {
  let resolvedRole = null;

  for (let i = 0; i < candidates.length; i += 1) {
    const raw = candidates[i];
    const items = Array.isArray(raw) ? raw : [raw];

    for (let j = 0; j < items.length; j += 1) {
      const normalized = normalizeSingleRoleCandidate(items[j]);
      if (normalized === 'CONFLICT' || normalized === 'UNKNOWN') {
        return normalized;
      }
      if (normalized) {
        if (resolvedRole && resolvedRole !== normalized) {
          return 'CONFLICT';
        }
        resolvedRole = normalized;
      }
    }
  }

  return resolvedRole;
};

export const extractRoleFromJwt = tokenPayload => {
  if (!tokenPayload || typeof tokenPayload !== 'object') {
    return null;
  }
  return combineRoleCandidates([tokenPayload.role, tokenPayload.roles, tokenPayload.roleId]);
};

export const extractRoleFromLoginInfoData = loginData => {
  if (!loginData || typeof loginData !== 'object') {
    return null;
  }
  return combineRoleCandidates([loginData.role, loginData.roles, loginData.roleId]);
};

export const getRoleResolutionState = loginInfo => {
  const authCookie = Cookies.get(Cookie.AUTH_TOKEN);
  const tokenPayload = authCookie ? parseJwt(authCookie) : null;
  const jwtRole = extractRoleFromJwt(tokenPayload);

  const loginStatus = loginInfo?.status;
  const isLoginLoading =
    !loginStatus || loginStatus === status.SHOULD_CALL_API || loginStatus === status.FETCHING;

  if (loginStatus === status.FAIL) {
    return { status: ROLE_RESOLUTION_STATUS.FAILED, role: null };
  }

  const reduxRole = extractRoleFromLoginInfoData(loginInfo?.details?.data);

  if (
    jwtRole === 'CONFLICT' ||
    jwtRole === 'UNKNOWN' ||
    reduxRole === 'CONFLICT' ||
    reduxRole === 'UNKNOWN'
  ) {
    return { status: ROLE_RESOLUTION_STATUS.DENIED, role: null };
  }

  if (jwtRole && reduxRole && jwtRole !== reduxRole) {
    return { status: ROLE_RESOLUTION_STATUS.DENIED, role: null };
  }

  if (isLoginLoading) {
    return { status: ROLE_RESOLUTION_STATUS.LOADING, role: null };
  }

  const finalRole = jwtRole || reduxRole;
  if (finalRole === PORTAL_ROLES.PHARMACY_OWNER || finalRole === PORTAL_ROLES.PHARMACIST) {
    return { status: ROLE_RESOLUTION_STATUS.RESOLVED, role: finalRole };
  }

  return { status: ROLE_RESOLUTION_STATUS.DENIED, role: null };
};

export const getDefaultRouteForRole = role => {
  if (role === PORTAL_ROLES.PHARMACY_OWNER) {
    return `/${pharmacyContexts.vpay}`;
  }
  if (role === PORTAL_ROLES.PHARMACIST) {
    return `/${pharmacyContexts.orders}`;
  }
  return '/login';
};

export const isVpayContext = context => VPAY_CONTEXTS.includes(context);

export const isRouteAllowedForRole = (role, isVpayRoute) => {
  if (role === PORTAL_ROLES.PHARMACY_OWNER) {
    return Boolean(isVpayRoute);
  }
  if (role === PORTAL_ROLES.PHARMACIST) {
    return !isVpayRoute;
  }
  return false;
};

export const getAllowedSidebarItems = (role, items = []) => {
  if (role === PORTAL_ROLES.PHARMACY_OWNER) {
    return items.filter(item => isVpayContext(item.context));
  }
  if (role === PORTAL_ROLES.PHARMACIST) {
    return items.filter(item => !isVpayContext(item.context));
  }
  return [];
};
